/**
 * Unified Loading System - Inline Loader Variant
 *
 * Compact inline spinner for buttons and inline actions.
 * Uses lucide-react Loader2 icon for consistency with existing Spinner.
 *
 * @example
 * ```tsx
 * <InlineLoader size="md" />
 * <InlineLoader size="lg" label="Loading..." />
 * ```
 */

import { Loader2 } from 'lucide-react';
import type { InlineLoaderProps, LoaderSize } from '../types';
import { LOADER_SIZES } from '../constants/loaderConfig';

/**
 * Utility for merging class names (lightweight alternative to clsx/classnames)
 */
function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Size mappings for the inline loader
 * Matches LOADER_SIZES from loaderConfig.ts
 */
const SIZE_STYLES: Record<LoaderSize, { icon: number; container: string }> = {
  sm: { icon: LOADER_SIZES.sm, container: 'gap-1.5' },
  md: { icon: LOADER_SIZES.md, container: 'gap-2' },
  lg: { icon: LOADER_SIZES.lg, container: 'gap-2' },
  xl: { icon: LOADER_SIZES.xl, container: 'gap-3' },
};

/**
 * InlineLoader - Compact inline spinner component
 *
 * Renders a spinning loader icon with optional label.
 * Supports multiple sizes and is fully accessible.
 */
export function InlineLoader({
  size = 'md',
  label,
  className,
  ariaLabel,
}: InlineLoaderProps) {
  const sizeConfig = SIZE_STYLES[size];
  const iconSize = sizeConfig.icon;

  // Determine aria label - prefer explicit ariaLabel, fall back to label, then default
  const accessibilityLabel = ariaLabel || label || 'Loading';

  return (
    <div
      className={cn(
        'inline-flex items-center',
        sizeConfig.container,
        // Using CSS variables for theme support
        'text-[var(--ats-loading-primary,#6366f1)]',
        'dark:text-[var(--ats-loading-primary-dark,#818cf8)]',
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={accessibilityLabel}
    >
      {/* Spinning icon */}
      <Loader2
        size={iconSize}
        className="animate-spin"
        aria-hidden="true"
      />

      {/* Optional label */}
      {label && (
        <span className="text-sm text-current">{label}</span>
      )}

      {/* Screen reader only text (always present for accessibility) */}
      <span className="sr-only">{accessibilityLabel}</span>
    </div>
  );
}

export default InlineLoader;
